import type {
	DashboardAmount,
	StoreHealth,
	StorePerformanceRow,
	StoreTrend,
} from "@khazzin/api-client";
import type { Locale } from "@khazzin/i18n";
import { formatMoney } from "@khazzin/i18n/money";
import type { ChartRow } from "../charts";
import type { Tone } from "../styles";

/**
 * Store Performance's arithmetic (EP13-S11), apart from the screens so it is
 * tested without rendering. The server composes and divides; this only lays
 * the figures out.
 */

/** The health chip's tone; the word is always written beside it. */
export const HEALTH_TONE: Record<StoreHealth, Tone> = {
	good: "success",
	watch: "warning",
	risk: "danger",
};

/**
 * The four a store's storage is always counted by, in the order the storage
 * meter draws them: a CBM position, the Large bin and the Small bin standard
 * units (a bin is counted by its storage unit), a shelf.
 */
export const STORE_UNIT_KINDS = ["cbm", "sut_std_large_bin", "sut_std_small_bin", "shelf"] as const;

/** Every currency's amount, joined; a dash with none. */
export function amountsText(amounts: DashboardAmount[], locale: Locale): string {
	if (amounts.length === 0) {
		return "—";
	}
	return amounts.map((a) => formatMoney(a, locale)).join(" · ");
}

/**
 * The storage meter's rows: each kind's share of positions out of 10,000, so
 * the store's projection (which has no capacity) draws the same meter.
 */
export function storageRows(row: StorePerformanceRow | undefined): ChartRow[] {
	const kinds = row?.storage?.kinds ?? [];
	const order = (kind: string) => {
		const i = (STORE_UNIT_KINDS as readonly string[]).indexOf(kind);
		return i < 0 ? STORE_UNIT_KINDS.length : i;
	};
	return [...kinds]
		.sort((a, b) => order(a.unitKind) - order(b.unitKind))
		.map((k) => ({
			kind: k.unitKind,
			used: k.utilizationBps ?? 0,
			total: 10000,
			occupied: k.occupied,
		}));
}

/** The trend's days: orders, and the fulfillment rate as a percentage (a gap with nothing received). */
export function scorecardTrendRows(trend: StoreTrend | undefined): ChartRow[] {
	return (trend?.days ?? []).map((d) => ({
		date: d.date,
		orders: d.orders,
		rate: d.fulfillmentRateBps === null ? null : d.fulfillmentRateBps / 100,
	}));
}

/** The ranking's sort keys: a number per column, rates with no figure last. */
export type StoreSortKey =
	| "rank"
	| "orders"
	| "fulfillment"
	| "onSla"
	| "returns"
	| "storage"
	| "fees"
	| "payout";

/** One row's figure for a column; a missing rate sorts below every figure. */
export function sortValue(row: StorePerformanceRow, key: StoreSortKey, currency: string): number {
	const amount = (amounts: DashboardAmount[]) =>
		amounts.find((a) => a.currency === currency)?.amountMinor ?? 0;
	const rate = (bps: number | null) => bps ?? -1;
	switch (key) {
		case "rank":
			return -(row.rank ?? Number.MAX_SAFE_INTEGER);
		case "orders":
			return row.orders;
		case "fulfillment":
			return rate(row.fulfillmentRateBps);
		case "onSla":
			return rate(row.onSlaRateBps);
		case "returns":
			return rate(row.returnRateBps);
		case "storage":
			return rate(row.storageUtilizationBps);
		case "fees":
			return amount(row.feesBilled);
		case "payout":
			return amount(row.netPayout);
	}
}

/** The rows by a column, the highest first (the rank's, the first place first). */
export function sortStores(
	rows: StorePerformanceRow[],
	key: StoreSortKey,
	currency: string,
): StorePerformanceRow[] {
	return [...rows].sort(
		(a, b) =>
			sortValue(b, key, currency) - sortValue(a, key, currency) ||
			a.storeName.localeCompare(b.storeName),
	);
}
